// Points the Firebase SDKs at the local emulators when the app is served by a
// local dev server.
//
// THE DATABASE. Whenever the page is served from localhost, the Realtime
// Database SDK talks to the local database emulator. That has always been true
// (it used to be inline in src/main.js). The port defaults to 9000, which is
// what firebase.json pins, and VUE_APP_DB_EMULATOR_PORT moves it. The sign-up
// browser suite (npm run test:e2e:signup) sets it, so its emulator can use a
// port of its own and never collide with a developer's emulator on 9000.
//
// AUTH is opt-in. With VUE_APP_AUTH_EMULATOR_PORT unset, an ordinary
// `npm run serve` keeps signing in to the real QA Auth. With it set, the Auth
// SDK is pointed at the auth emulator on 127.0.0.1.
//
// The auth wiring must NOT create the Auth instance. On the web nothing
// creates Auth at startup: the first page code that asks for it does, later,
// and that ordering decides whether the database or a page registers its
// token observer first. The repo's older local-testing recipes called
// connectAuthEmulator on an Auth instance created at startup, which changed
// that order and hid a production bug where the first-sign-in profile write
// went out before the database had the new user's token
// (docs/superpowers/specs/2026-09-24-first-sign-in-seed-auth-order-design.md).
// So this module registers a callback on the app's "auth" component provider.
// The provider runs the callback when the app itself first creates Auth, and
// registering it creates nothing. tests/unit/emulatorWiring.test.js pins that.
//
// Both halves are skipped inside the Capacitor native shell. Its WebView serves
// the bundle from localhost too, and .gitignore ignores .env.* files, so a
// stray VUE_APP_AUTH_EMULATOR_PORT in a developer's local env file could
// otherwise reach a native build and point device sign-in at 127.0.0.1. The
// provider callback also fires for main.js's native initializeAuth call, so the
// exclusion has to happen here, before anything is registered.
//
// Everything is addressed as 127.0.0.1, never "localhost". The auth emulator's
// redirect flow stores its result in the emulator page's sessionStorage and
// reads it back from an iframe inside the app page. If the app and the
// emulator were on different hosts, the browser's third-party storage
// partitioning would give that iframe different storage and the redirect would
// never complete.

import { _getProvider } from "firebase/app";
import { connectAuthEmulator } from "firebase/auth";
import { getDatabase, connectDatabaseEmulator } from "firebase/database";

export const DEFAULT_DATABASE_EMULATOR_PORT = 9000;

const EMULATOR_HOST = "127.0.0.1";

/**
 * Whether this page is served by a local dev server.
 *
 * Returns false inside the Capacitor native shell before it looks at the
 * hostname, because the native WebView serves the bundle from localhost.
 */
export function isLocalhost() {
  if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.() === true) return false;
  return location.hostname === "localhost" || location.hostname === "127.0.0.1";
}

/**
 * Reads a port number from a build-time environment value.
 *
 * Returns null when the value is unset or empty. Returns undefined when it is
 * set to something that is not a whole number from 1 to 65535, so the caller
 * can tell "not configured" apart from "configured wrongly".
 */
function readPort(value) {
  if (value === undefined || value === null || value === "") return null;
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return undefined;
  const port = Number(text);
  return port >= 1 && port <= 65535 ? port : undefined;
}

/**
 * Wires the database emulator, and the auth emulator when it is opted in.
 *
 * Called once from src/main.js, right after initializeApp.
 *
 * @param {import("firebase/app").FirebaseApp} firebaseApp
 */
export function wireEmulators(firebaseApp) {
  if (!isLocalhost()) return;

  // VUE_APP_* values are inlined by webpack at build time, so each one must be
  // read as a literal `process.env.NAME` expression.
  let databasePort = readPort(process.env.VUE_APP_DB_EMULATOR_PORT);
  if (databasePort === undefined) {
    console.warn(
      `VUE_APP_DB_EMULATOR_PORT is not a valid port number; using ${DEFAULT_DATABASE_EMULATOR_PORT}.`
    );
  }
  if (!databasePort) databasePort = DEFAULT_DATABASE_EMULATOR_PORT;
  // isLocalhost() is false inside the native shell, so this is always the web
  // host today. The native branch is kept from the code this replaced.
  const databaseHost =
    window.Capacitor?.isNativePlatform?.() === true ? "10.0.2.2" : EMULATOR_HOST;
  console.log(
    `Localhost detected! Setting up local database emulator at ${databaseHost}:${databasePort}.`
  );
  connectDatabaseEmulator(getDatabase(firebaseApp), databaseHost, databasePort);

  const authPort = readPort(process.env.VUE_APP_AUTH_EMULATOR_PORT);
  if (authPort === undefined) {
    console.warn(
      "VUE_APP_AUTH_EMULATOR_PORT is not a valid port number; the auth emulator is not wired, so sign-in uses the real QA Auth."
    );
    return;
  }
  if (authPort === null) return;
  console.log(`Auth emulator opted in at ${EMULATOR_HOST}:${authPort}.`);
  // The provider runs this when the app first creates Auth. If Auth already
  // existed it would run immediately, but nothing creates Auth before this
  // module runs on the web.
  _getProvider(firebaseApp, "auth").onInit((auth) => {
    try {
      // disableWarnings stops the SDK from pinning its "Running in emulator
      // mode" banner to the bottom of every page, where it sits above
      // bottom-anchored buttons and can intercept a browser test's clicks.
      connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${authPort}`, {
        disableWarnings: true,
      });
    } catch (error) {
      // The provider swallows a callback's errors, so report it here.
      console.error("Could not connect the auth emulator", error);
    }
  });
}
